/**
 * Keyboard and focus behavior for a modal dialog panel.
 *
 * While `open`: page scroll is locked, Escape calls `onClose` (when given),
 * and Tab and Shift+Tab cycle within `panelRef`. With dialogs stacked, only
 * the most recently opened one handles Escape and the Tab wrap, so a confirm
 * over a form keeps the keyboard to itself and a closing dialog never swallows
 * the keys of the one beneath. Opening moves focus to the first control inside
 * `initialRef` (the dialog body, so a form starts on its first field), else
 * the first control in the panel, else the panel itself, which needs
 * `tabIndex={-1}`. Closing returns focus to the element that was focused
 * before the dialog opened, when it is still in the document and not inside a
 * closing (inert) dialog. The trap is a keydown wrap rather than a native
 * `<dialog>`, so existing backdrop styling and jsdom-based tests keep working.
 */

import { useEffect, useId, useRef, type RefObject } from "react";

/** The ids of the open dialogs, oldest first; the last one owns the keyboard. */
const openDialogs: string[] = [];

/** The keyboard-reachable controls the focus trap cycles through. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
}

export function useDialogFocus({
  open,
  panelRef,
  initialRef,
  onClose,
}: {
  open: boolean;
  panelRef: RefObject<HTMLElement | null>;
  initialRef?: RefObject<HTMLElement | null>;
  /** Escape handler; omit for dialogs that only their own buttons dismiss. */
  onClose?: () => void;
}): void {
  // Read through a ref so a new handler identity doesn't re-run the trap effect.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  // Register on the stack only on open and close, so a re-render never moves
  // this dialog above one opened after it.
  const id = useId();
  useEffect(() => {
    if (!open) return;
    openDialogs.push(id);
    return () => {
      openDialogs.splice(openDialogs.indexOf(id), 1);
    };
  }, [open, id]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent): void => {
      if (openDialogs.at(-1) !== id) return;
      if (e.key === "Escape") {
        closeRef.current?.();
        return;
      }
      const panel = panelRef.current;
      if (e.key !== "Tab" || panel === null) return;
      const items = focusables(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && panel.contains(active);
      const first = items[0]!;
      const last = items[items.length - 1]!;
      // Leaving the panel in either direction wraps to its other end; focus
      // that strayed outside (a backdrop click) re-enters at that end too.
      if (e.shiftKey ? active === first || !inside : active === last || !inside) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, panelRef, id]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const initial = initialRef?.current ?? null;
    const target =
      (initial === null ? undefined : focusables(initial)[0]) ??
      (panel === null ? undefined : focusables(panel)[0]) ??
      panel;
    target?.focus();
    return () => {
      // An opener inside another closing dialog (a confirm over a form that
      // closes with it) is inert, so that dialog returns focus to its own opener.
      if (opener?.isConnected && opener.closest("[inert]") === null) opener.focus();
    };
  }, [open, panelRef, initialRef]);
}
